import React from 'react'
import styled, { keyframes } from 'styled-components'
import { FloatingFocusManager } from '@floating-ui/react-dom-interactions'
import { CSSTransition } from 'react-transition-group'
import { getGifHeight } from '@giphy/js-util'

import { Pointer, PointerProps } from './pointer'
import type { GifPopoverContext } from './types'

export type GifPopoverProps = {
    context: GifPopoverContext
    gifWidth: number
    PointerProps?: Partial<PointerProps>
} & React.ComponentPropsWithRef<'div'>

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  50% {
    opacity: 1;
  }
`

const zoomOut = keyframes`
  from {
    opacity: 1;
  }

  50% {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  to {
    opacity: 0;
  }
`

const Container = styled.div<{ open: boolean }>`
    box-sizing: border-box;
    filter: drop-shadow(0px 18px 30px black);
    max-width: calc(100% - 8px);

    &.zoom-enter {
        animation: ${zoomIn} 150ms ease-in;
    }

    &.zoom-exit {
        animation: ${zoomOut} 150ms ease-out;
    }
`

const InnerContainer = styled.div`
    position: relative;

    & > .${Pointer.className} {
        z-index: 0;
        & + * {
            z-index: 9999;
        }
    }
`

export function GifPopover(props: GifPopoverProps) {
    const { children, gifWidth, PointerProps, context, ...other } = props
    const { arrowRef, gif, floating, open, refs, strategy, x, y } = context
    const { getFloatingProps } = context.interactions
    const gifHeight = gif ? getGifHeight(gif, gifWidth) : gifWidth

    return (
        <CSSTransition
            classNames="zoom"
            in={open}
            mountOnEnter={false}
            nodeRef={refs.floating}
            timeout={150}
            unmountOnExit={true}
        >
            <FloatingFocusManager context={context} order={['reference', 'content']} returnFocus={false}>
                <Container
                    ref={floating}
                    open={open}
                    {...getFloatingProps({
                        ...other,
                        style: { position: strategy, top: y ?? 0, left: x ?? 0, ...other?.style },
                    })}
                >
                    <InnerContainer>
                        <Pointer
                            ref={arrowRef}
                            context={context}
                            height={gifHeight}
                            width={gifWidth - 8}
                            {...PointerProps}
                        />
                        {children}
                    </InnerContainer>
                </Container>
            </FloatingFocusManager>
        </CSSTransition>
    )
}
